import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'ramda/src/isEmpty';
import isNil from 'ramda/src/isNil';
import memoizeWith from 'ramda/src/memoizeWith';
import Checkbox from '@fountain/ui-kit/components/checkbox';
import Select from '@fountain/ui-kit/components/select';
import LocationPicker from 'shared/location-picker';
import { Button, Form } from '@fountain/ui-kit/components/';

import './styles.pcss';

const DISTANCE_OPTIONS = ['5', '10', '15', '25', '50', '100'];
const DISTANCE_DEFAULT = DISTANCE_OPTIONS[DISTANCE_OPTIONS.length - 2];

export default class OpeningsDirectoryIndexFilters extends PureComponent {
  static propTypes = {
    accountSlug: PropTypes.string.isRequired,
    apiKeys: PropTypes.shape({
      googleMaps: PropTypes.string,
      googlePlaces: PropTypes.string,
    }).isRequired,
    brandSlug: PropTypes.string,
    clearAllFilters: PropTypes.func.isRequired,
    countryCode: PropTypes.string,
    loadOpenings: PropTypes.func.isRequired,
    location: PropTypes.shape({
      search: PropTypes.string.isRequired,
    }).isRequired,
    openings: PropTypes.arrayOf(PropTypes.shape({
      locationAddress: PropTypes.string,
    })),
    params: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
    positions: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    })),
    preview: PropTypes.bool,
    translations: PropTypes.shape({
      view: PropTypes.string,
      cityOrZipCodePlaceholder: PropTypes.string,
      clearAllSelections: PropTypes.string,
      jobOpenings: PropTypes.string,
      location: PropTypes.string,
      positions: PropTypes.string,
      radius: PropTypes.string,
      selectPosition: PropTypes.string,
      nonePositions: PropTypes.string,
      showRemoteJobsOnly: PropTypes.string,
      locationIsRequired: PropTypes.string,
      noResultsInRadius: PropTypes.string,
      useCurrentLocation: PropTypes.string,
      hour: PropTypes.string,
    }).isRequired,
    ui: PropTypes.shape({
      isLoading: PropTypes.bool,
    }).isRequired,
    updateParams: PropTypes.func.isRequired,
  }

  static defaultProps = {
    brandSlug: null,
    countryCode: null,
    openings: [],
    params: {},
    positions: [],
    preview: false,
  }

  componentDidMount() {
    if (this.props.preview) {
      return;
    }

    this._load(this.props.params);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.location.search === this.props.location.search) {
      return;
    }

    this._load(nextProps.params);
  }

  _load = (params = {}) => {
    const {
      accountSlug,
      brandSlug,
      countryCode,
      loadOpenings,
    } = this.props;

    const locationExternalId = params.location_external_id;

    loadOpenings({
      accountSlug,
      brandSlug,
      countryCode,
      locationExternalId,
      params,
    });
  }

  _getUnits = () => {
    const { countryCode } = this.props;

    return (countryCode && countryCode !== 'US') ? 'km' : 'mi';
  }

  _getDistanceOptions = () => {
    const units = this._getUnits();

    return DISTANCE_OPTIONS.map(distance => `${distance}${units}`);
  }

  _changeLocation = (value) => {
    const { params, updateParams } = this.props;
    const units = this._getUnits();
    const newDistance = value ? params.distance || `${DISTANCE_DEFAULT}${units}` : null;

    updateParams({
      location_external_id: params.location_external_id || null,
      lat: value && value.latitude,
      lon: value && value.longitude,
      distance: newDistance,
      page: null,
    });
  }

  _getLocation = memoizeWith(
    (lat, lng) => `${lat}-${lng}`,
    (lat, lon) => {
      if (isNil(lat) || isNil(lon)) {
        return undefined;
      }

      return {
        latitude: parseFloat(lat),
        longitude: parseFloat(lon),
      };
    }
  )

  render() {
    const {
      apiKeys,
      openings,
      params,
      positions,
      translations,
      ui,
      updateParams,
      clearAllFilters,
    } = this.props;

    const remoteJobs = openings.filter(opening => !opening.locationAddress);
    const allJobsAreRemote = remoteJobs.length === openings.length && !params.remote;
    const shouldRenderRemoteFilter = !allJobsAreRemote && remoteJobs.length > 0;
    const distanceOptions = this._getDistanceOptions();

    return (
      <div className="openings-directory-index-filters__container">
        <div className="openings-directory-index-filters">
          <div className="openings-directory-index-filters__group openings-directory-index-filters__group_location">
            <Form.Label className="openings-directory-index-filters__radius">{translations.radius}</Form.Label>
            <Form.Label className="openings-directory-index-filters__location">{translations.location}</Form.Label>
            <Select
              className="openings-directory-index-filters__distance"
              fullWidth
              disabled={ui.isLoading}
              id="distance"
              onChange={value => updateParams({ distance: value, page: null })}
              placeholder="Select"
              value={params.distance}
            >
              {distanceOptions.map(distance => (
                <Select.Option key={distance} value={distance}>{distance}</Select.Option>
              ))}
            </Select>
            <LocationPicker
              apiKey={apiKeys.googleMaps}
              id="address"
              className="openings-directory-index-filters__location"
              onChange={this._changeLocation}
              disabled={ui.isLoading}
              value={this._getLocation(params.lat, params.lon)}
              translations={translations}
            />
          </div>
          <div className="openings-directory-index-filters__group openings-directory-index-filters__group_position">
            <Form.Label>{translations.position}</Form.Label>
            <Select
              disabled={isEmpty(positions) || ui.isLoading}
              fullWidth
              id="position_id"
              onChange={value => updateParams({ page: null, position_id: value })}
              placeholder={translations.selectPosition}
              value={params.position_id}
            >
              <Select.Option value={undefined}>{translations.nonePositions}</Select.Option>
              {positions.map(position => (
                <Select.Option key={position.id} value={position.id}>{position.name}</Select.Option>
              ))}
            </Select>
          </div>

          {shouldRenderRemoteFilter && (
            <div className="openings-directory-index-filters__group openings-directory-index-filters__show-remote-jobs">
              <Checkbox
                checked={params.remote === 'true'}
                disabled={ui.isLoading}
                id="remote"
                onChange={value => updateParams({ page: null, remote: value || undefined })}
              >
                {translations.showRemoteJobsOnly}
              </Checkbox>
            </div>
          )}
          <div className="openings-directory-index-filters__clear-all-filters">
            <Button className="clear-all-button" theme="text" size="large" onClick={clearAllFilters}>{translations.clearAllSelections}</Button>
          </div>
        </div>
        <div>
          <img alt="Powered by Google" className="openings-directory-index-filters__powered-by-google" src="https://s3-us-west-1.amazonaws.com/static.fountain.com/powered_by_google_on_white.png" />
        </div>
      </div>
    );
  }
}
